import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { RootState, AppDispatch } from "../../store/store";
import { Typography } from "@material-tailwind/react";
import moment from "moment";
import { toast } from "react-toastify";
import { debounce } from "lodash";
import {
  fetchNewsletterSubscribers,
  exportAllNewsletterSubscribers,
  deleteNewsletterSubscriber,
} from "../../store/NewsletterSlice";
import { NewsletterSubscriber } from "../../interfaces/Newsletter";
import { exportToExcel } from "../../utils/excelExport";
import ExportDropdown from "../ExportDropdown";

const NewsletterView = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [isExporting, setIsExporting] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { data, loading } = useSelector((state: RootState) => state.newsletter);

  const TABLE_HEAD = [
    "#",
    t("adminNewsletter.table.email", "EMAIL"),
    t("adminNewsletter.table.status", "STATUS"),
    t("adminNewsletter.table.dateSubscribed", "DATE SUBSCRIBED"),
    t("adminNewsletter.table.actions", "ACTIONS"),
  ];

  const debouncedSearchHandler = useRef(
    debounce((query: string) => {
      setDebouncedSearch(query);
      setPage(1);
    }, 400)
  ).current;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearch(val);
    debouncedSearchHandler(val);
  };

  const handleClearSearch = () => {
    setSearch("");
    setDebouncedSearch("");
    setPage(1);
  };

  useEffect(() => {
    dispatch(fetchNewsletterSubscribers({ page, limit, search: debouncedSearch }));
  }, [dispatch, page, limit, debouncedSearch]);

  const handleDelete = async (id: string, email: string) => {
    if (window.confirm(t("adminNewsletter.confirmDelete", `Are you sure you want to remove ${email} from subscribers?`))) {
      setDeletingId(id);
      try {
        await dispatch(deleteNewsletterSubscriber(id)).unwrap();
        toast.success(t("adminNewsletter.deleteSuccess", "Subscriber removed successfully"));
      } catch (err: any) {
        toast.error(err || t("adminNewsletter.deleteError", "Failed to remove subscriber"));
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleExport = async (format: "xlsx" | "csv") => {
    setIsExporting(true);
    try {
      const result = await dispatch(exportAllNewsletterSubscribers()).unwrap();
      if (!result || result.length === 0) {
        toast.info(t("adminNewsletter.noSubscribersExport", "No subscribers to export"));
        return;
      }

      const headers = ["ID", "Email", "Status", "Date Subscribed"];
      const rawRows = result.map((sub: NewsletterSubscriber) => [
        sub.id || "",
        sub.email || "",
        sub.status || "",
        sub.createdAt ? moment(sub.createdAt).format("YYYY-MM-DD HH:mm:ss") : "",
      ]);

      const dateStamp = moment().format("YYYY-MM-DD");

      if (format === "xlsx") {
        await exportToExcel({
          filename: `newsletter_subscribers_${dateStamp}`,
          sheetName: "Subscribers",
          headers,
          rows: rawRows,
          columnWidths: [
            { wch: 26 }, // ID
            { wch: 32 }, // Email
            { wch: 14 }, // Status
            { wch: 22 }, // Date Subscribed
          ],
        });
      } else {
        const rows = rawRows.map((r) =>
          r.map((val) => `"${String(val ?? "").replace(/"/g, '""')}"`).join(",")
        );
        const csvContent = "\uFEFF" + [headers.join(","), ...rows].join("\r\n");
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `newsletter_subscribers_${dateStamp}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }

      toast.success(t("adminNewsletter.exportSuccess", "Subscribers exported successfully!"));
    } catch (error: any) {
      toast.error(error?.message || error || t("adminNewsletter.exportError", "Failed to export subscribers"));
    } finally {
      setIsExporting(false);
    }
  };

  const subscribers = data?.subscribers || [];
  const total = data?.total || 0;
  const totalPages = data?.totalPages || 1;

  return (
    <div className="w-full h-full flex flex-col overflow-hidden">
      {/* Search & Export Toolbar */}
      <div className="p-4 bg-white border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
        <div className="relative w-72">
          <input
            type="text"
            value={search}
            onChange={handleSearchChange}
            placeholder={t("adminNewsletter.searchPlaceholder", "Search subscriber email...")}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#B4804A]"
          />
          {search && (
            <button
              onClick={handleClearSearch}
              className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm cursor-pointer"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-500 font-medium">
            {t("adminNewsletter.totalCount", "Total:")}{" "}
            <strong className="text-gray-800">{total}</strong>
          </span>
          <ExportDropdown
            onExport={handleExport}
            isExporting={isExporting}
            disabled={total === 0}
            variant="primary"
            align="right"
          />
        </div>
      </div>

      {/* Table */}
      <div className="w-full flex-1 overflow-x-auto overflow-y-auto">
        <table className="w-full min-w-[650px] table-auto text-left">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-[#F8F9FA] text-left border-b-2 border-gray-100">
              {TABLE_HEAD.map((head, i) => (
                <th
                  key={i}
                  className="py-4 px-6 font-bold text-[#7E8299] uppercase text-xs whitespace-nowrap"
                >
                  <Typography
                    variant="small"
                    className="font-semibold leading-none text-[#7E8299] opacity-70 whitespace-nowrap"
                  >
                    {head}
                  </Typography>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-500 font-medium">
                  {t("common.loading", "Loading...")}
                </td>
              </tr>
            ) : subscribers.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-12 text-gray-500 font-medium">
                  {t("adminNewsletter.noSubscribers", "No newsletter subscribers found")}
                </td>
              </tr>
            ) : (
              subscribers.map((sub, index) => (
                <tr
                  key={sub.id || index}
                  className="hover:bg-gray-50/50 transition-colors"
                >
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-500 font-medium">
                    {(page - 1) * limit + index + 1}
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <Typography
                      variant="small"
                      color="black"
                      className="font-medium text-sm"
                    >
                      {sub.email}
                    </Typography>
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        sub.status === "subscribed"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {sub.status === "subscribed"
                        ? t("adminNewsletter.subscribed", "Subscribed")
                        : t("adminNewsletter.unsubscribed", "Unsubscribed")}
                    </span>
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap text-sm text-gray-600">
                    {sub.createdAt
                      ? moment(sub.createdAt).format("DD MMM, YYYY HH:mm")
                      : "-"}
                  </td>
                  <td className="py-4 px-6 whitespace-nowrap">
                    <button
                      onClick={() => handleDelete(sub.id, sub.email)}
                      disabled={deletingId === sub.id}
                      className="text-red-500 hover:text-red-700 font-semibold text-sm transition-colors cursor-pointer"
                    >
                      {deletingId === sub.id
                        ? t("adminNewsletter.deleting", "Removing...")
                        : t("adminNewsletter.delete", "Remove")}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      {totalPages > 1 && (
        <div className="p-4 bg-white border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-500">
            {t("adminNewsletter.page", "Page")} {page} / {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1 || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminNewsletter.prev", "Previous")}
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || loading}
              className="px-3 py-1 text-xs border rounded hover:bg-gray-50 disabled:opacity-40 cursor-pointer"
            >
              {t("adminNewsletter.next", "Next")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewsletterView;
